/**
 * CircularCmfGuidePage — Circular CMF Guide landing / category page (CX-2725)
 *
 * Route: /circular-cmf-guide   (gated by the `circularCmfGuide` feature flag)
 *
 * PAGE STRUCTURE:
 *   1. Breadcrumb — Homepage / Inspiration / Circular CMF Guide
 *   2. TrendHero  — reused cinematic hero, title + intro + "EXPLORE GUIDE" CTA
 *   3. Overview   — "Circular CMF — An Overview" text block
 *   4. Chapters   — "Four Chapters" grid of chapter cards → sub-pages
 *
 * Content comes from the static `cmfGuideData` registry today; swap for a
 * `/v0/content/circular-cmf-guide` hook later (same shape — see cmfGuideData.ts).
 *
 * EMPTY STATE (CX-2725 AC): sections with no content are hidden; the page never
 * errors when a section is empty.
 */

import { Breadcrumb } from "components/navigation";
import { TrendHero } from "pages/trend/components/TrendHero";
import { useTranslation } from "react-i18next";
import { cmfGuideData } from "./cmfGuideData";
import { ChapterCard } from "./components/ChapterCard";
import "./CircularCmfGuidePage.css";

export const CircularCmfGuidePage = () => {
  const { t } = useTranslation();
  const { hero, overview, chapters } = cmfGuideData;

  const hasOverview = overview.paragraphs.some((p) => p.trim().length > 0);
  const hasChapters = chapters.length > 0;

  return (
    <div className="cmf-guide-page">
      {/* Breadcrumb — Homepage / Circular CMF Guide */}
      <Breadcrumb
        items={[
          { label: t("common_homepage", "Homepage"), to: "/", home: true },
          { label: t("cmf_guide_breadcrumb", "Circular CMF Guide") },
        ]}
      />

      {/* Cinematic hero — reuses the Trend template hero */}
      <TrendHero
        heroImage={hero.image}
        title={t("cmf_guide_hero_title", hero.title)}
        chip={t("cmf_guide_chip", hero.subtitle)}
        description={t("cmf_guide_hero_intro", hero.intro)}
        exploreLabel={t("cmf_guide_explore_cta", hero.ctaLabel)}
        tagLabel={t("cmf_guide_tag", "CMF Library")}
        showExplore
        truncate
      />

      {/* Overview — hidden when empty (AC: empty state) */}
      {hasOverview && (
        <section className="cmf-guide-page__overview">
          <div className="cmf-guide-page__overview-inner">
            <h2 className="cmf-guide-page__overview-title">
              {t("cmf_guide_overview_title", overview.title)}
            </h2>
            {overview.paragraphs.map((paragraph) => (
              <p key={paragraph} className="cmf-guide-page__overview-text">
                {paragraph}
              </p>
            ))}
          </div>
        </section>
      )}

      {/* Four Chapters — hidden when empty (AC: empty state) */}
      {hasChapters && (
        <section className="cmf-guide-page__chapters">
          <div className="cmf-guide-page__chapters-header">
            <h2 className="cmf-guide-page__chapters-title">
              {t("cmf_guide_chapters_title", "Four Chapters")}
            </h2>
            <span className="cmf-guide-page__chapters-subtitle">
              {t("cmf_guide_chapters_subtitle", "Circular design principles")}
            </span>
          </div>
          <div className="cmf-guide-page__chapters-grid">
            {chapters.map((chapter) => (
              <ChapterCard key={chapter.slug} chapter={chapter} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
};
